// Per-Business-Unit branding + template-selection hooks (Templating epic). Reads
// return the BU's overrides (null fields inherit the tenant); writes upsert the
// branding or pin/clear one surface/kind. The render + send resolvers already
// walk BU → tenant, so an unset value simply uses the tenant default.
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPut, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys, invoiceKeys } from '@/lib/queryKeys';
import type {
  BusinessUnitBranding,
  BusinessUnitDocSurface,
  BusinessUnitDocTemplateSelection,
  BusinessUnitEmailTemplateSelection,
  UpsertBusinessUnitBranding,
} from '@/types/businessUnitBranding';
import type { EmailTemplateKind } from '@/types/emailTemplate';

const brandingKey = (id: string) => [...schedulerKeys.all(), 'businessUnit', id, 'branding'];
const docTemplatesKey = (id: string) => [
  ...schedulerKeys.all(),
  'businessUnit',
  id,
  'docTemplates',
];
const emailTemplatesKey = (id: string) => [
  ...schedulerKeys.all(),
  'businessUnit',
  id,
  'emailTemplates',
];

// ── Branding ────────────────────────────────────────────────────────────────

export function useBusinessUnitBranding(id: string | null) {
  return useApiQuery<BusinessUnitBranding | null>({
    queryKey: brandingKey(id ?? 'none'),
    enabled: !!id,
    queryFn: () =>
      apiGet<BusinessUnitBranding | null>(
        operationsClient,
        schedulerEndpoints.businessUnitBranding(id!),
      ),
  });
}

export function useUpsertBusinessUnitBranding(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<BusinessUnitBranding, UpsertBusinessUnitBranding>({
    mutationFn: (dto) =>
      apiPut<BusinessUnitBranding, UpsertBusinessUnitBranding>(
        operationsClient,
        schedulerEndpoints.businessUnitBranding(id),
        dto,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(brandingKey(id), data);
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.details() });
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('document-layout'),
      });
    },
    showErrorToast: true,
  });
}

// ── Document-design selections ────────────────────────────────────────────────

export function useBusinessUnitDocTemplates(id: string | null) {
  return useApiQuery<BusinessUnitDocTemplateSelection[]>({
    queryKey: docTemplatesKey(id ?? 'none'),
    enabled: !!id,
    queryFn: () =>
      apiGet<BusinessUnitDocTemplateSelection[]>(
        operationsClient,
        schedulerEndpoints.businessUnitDocTemplates(id!),
      ),
  });
}

export function useSetBusinessUnitDocTemplate(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    BusinessUnitDocTemplateSelection[],
    { surface: BusinessUnitDocSurface; docTemplateId: string | null }
  >({
    mutationFn: (body) =>
      apiPut<
        BusinessUnitDocTemplateSelection[],
        { surface: BusinessUnitDocSurface; docTemplateId: string | null }
      >(operationsClient, schedulerEndpoints.businessUnitDocTemplates(id), body),
    onSuccess: (data) => {
      queryClient.setQueryData(docTemplatesKey(id), data);
      toast.success('Design updated for this business unit');
    },
    showErrorToast: true,
  });
}

// ── Email selections ──────────────────────────────────────────────────────────

export function useBusinessUnitEmailTemplates(id: string | null) {
  return useApiQuery<BusinessUnitEmailTemplateSelection[]>({
    queryKey: emailTemplatesKey(id ?? 'none'),
    enabled: !!id,
    queryFn: () =>
      apiGet<BusinessUnitEmailTemplateSelection[]>(
        operationsClient,
        schedulerEndpoints.businessUnitEmailTemplates(id!),
      ),
  });
}

export function useSetBusinessUnitEmailTemplate(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    BusinessUnitEmailTemplateSelection[],
    { kind: EmailTemplateKind; emailTemplateId: string | null }
  >({
    mutationFn: (body) =>
      apiPut<
        BusinessUnitEmailTemplateSelection[],
        { kind: EmailTemplateKind; emailTemplateId: string | null }
      >(operationsClient, schedulerEndpoints.businessUnitEmailTemplates(id), body),
    onSuccess: (data) => {
      queryClient.setQueryData(emailTemplatesKey(id), data);
      toast.success('Email updated for this business unit');
    },
    showErrorToast: true,
  });
}
